import React, { useEffect, useRef } from "react";
import { STRAIN_USER_REVIEW_DISCLAIMER } from "constants/disclaimers";
import { ITEMS_PER_STRAIN_PAGE } from "constants/strains";
import { NextPage, NextPageContext } from "next";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import getStrainListHeader from "api/requests/getStrainListHeader";
import getStrainLists from "api/requests/getStrainLists";
import { Strain } from "custom-types/Strain";
import { StrainFilters, StrainListHeaderProps } from "custom-types/StrainList";
import useFilters from "hooks/useFilters";
import useUserFavorites from "redux/hooks/useUserFavorites";
import { getCoordinates } from "redux/selectors/location";
import { getUserLocationInfoFromCookie } from "utils/getUserLocationFromCookie";
import { getCountryCodeHeader } from "utils/requestContext";

import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import MedicalDisclaimers from "components/MedicalDisclaimers";
import MenuFilters from "components/MenuFilters";
import useMenuFilterSearch from "components/MenuFilters/hooks/useMenuFilterSearch";
import { MenuPage, Response } from "components/MenuFilters/types/MenuFilters";
import StrainListCountSort from "components/StrainList/StrainListCountSort";
import StrainListHead from "components/StrainList/StrainListHead";
import StrainListHeader from "components/StrainList/StrainListHeader";
import StrainListV2 from "components/StrainListV2";

type StrainPageProps = {
  metadata: StrainListHeaderProps;
  filters: StrainFilters;
  menu?: Response;
};

const StrainsPage: NextPage<StrainPageProps> = ({ metadata, menu }) => {
  const { useInitiateFavorites } = useUserFavorites();
  useInitiateFavorites();

  const {
    query: { name: pageName, page, sort },
  } = useRouter();

  const isMounted = useRef(false);
  const {
    searchStrainMenuItems,
    results: { menuItems, availableFilters, availableSorts: _availableSorts },
    totalPages: totalCount,
    loading,
    menuFilterMap,
  } = useMenuFilterSearch(menu);
  const { filterValues } = useFilters();
  const coordinates = useSelector(getCoordinates);

  useEffect(() => {
    // This check is here to prevent a redundant call to fetch data on initial mount.
    // We only want to fetch new data if menu is a falsy value or when dependencies change.
    if (isMounted.current || !menu) {
      searchStrainMenuItems(
        filterValues,
        (Number(typeof page === "string" ? page : "1") - 1) *
          ITEMS_PER_STRAIN_PAGE,
        ITEMS_PER_STRAIN_PAGE,
        coordinates,
        typeof sort === "string" ? sort : undefined
      );
    }
    isMounted.current = true;
  }, [searchStrainMenuItems, filterValues, page, coordinates]);

  return (
    <>
      <MenuFilters
        menuFilterMap={menuFilterMap}
        availableFilters={availableFilters}
        menuPage={MenuPage.strains}
        loading={loading}
        renderHeader={() => (
          <>
            <StrainListHead
              description={
                metadata.meta_description ||
                "Browse the most comprehensive marijuana strain database on the web! Read strain reviews, discover new strains, and learn more about your favorites."
              }
              title={metadata.meta_title || "All strains"}
            />
            <StrainListHeader
              hero={metadata}
              displayTitle={metadata.title || "All strains"}
              textContainerStyle="-ml-6 md:-ml-1"
            />
          </>
        )}
      >
        <StrainListCountSort totalCount={totalCount} />
        <MedicalDisclaimers
          usDisclaimer={STRAIN_USER_REVIEW_DISCLAIMER}
          className="mb-lg"
        />

        <StrainListV2
          adPath="/13212770/strain/explore_playlist"
          adTarget={(pageName as string) || "strains"}
          loading={loading}
          totalCount={totalCount}
          strains={menuItems as Strain[]}
        />
      </MenuFilters>
      <Breadcrumbs>
        <Breadcrumb currentLocation href="/strains">
          Strains
        </Breadcrumb>
      </Breadcrumbs>
    </>
  );
};

export const getServerSideProps = async (context: NextPageContext) => {
  const countryCode = getCountryCodeHeader(context);
  const location = getUserLocationInfoFromCookie(context.req?.headers.cookie);

  const { latitude: lat, longitude: lon } = location?.coordinates ?? {};

  const { metadata, menu } = await Promise.allSettled([
    getStrainListHeader(countryCode, "base", undefined),
    getStrainLists({ enableNewFilters: true }, 0, ITEMS_PER_STRAIN_PAGE, {
      lat,
      lon,
    }),
  ]).then(([metadata, strainLists]) => ({
    menu: {
      availableFilters:
        strainLists.status === "fulfilled"
          ? strainLists.value.metadata.availableFilters
          : [],
      availableSorts:
        strainLists.status === "fulfilled"
          ? strainLists.value.metadata.availableSorts
          : [],
      menuItems:
        strainLists.status === "fulfilled" ? strainLists.value.strains : [],
      totalItems:
        strainLists.status === "fulfilled"
          ? strainLists.value.metadata.totalCount
          : 0,
    },
    metadata: metadata.status === "fulfilled" ? metadata.value : {},
  }));

  return {
    props: {
      menu,
      metadata,
    },
  };
};

export default StrainsPage;
